import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/content';

/** Real product pages — swipe on touch, arrows and dots on desktop. */
export function InsidePagesCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = insidePages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (slide) track.scrollTo({
      left: slide.offsetLeft,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      if (atEnd) {
        setActive(total - 1);
        return;
      }
      let closest = 0;
      slides.forEach((slide, index) => {
        if (Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft)) {
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, [total]);
  return <div className="relative mx-auto max-w-5xl" aria-roledescription="carousel" aria-label="Pages from Hair Instructor Ready" data-id="element-17" data-mp="3jtmwy8tn8cay">
      <div ref={trackRef} className="no-scrollbar relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2" data-id="element-18" data-mp="3toyaintbfdh4">
        {insidePages.map((page, index) => <figure key={page.src} className="w-[82%] flex-none snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]" aria-roledescription="slide" aria-label={`${index + 1} of ${total}: ${page.caption}`} data-id="element-19" data-mp="1jkop8i3vbws" data-mpi={index}>
            <img src={page.src} alt={`${page.caption} — a real page from Hair Instructor Ready`} width={720} height={900} loading={index < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="block aspect-[4/5] h-auto w-full select-none rounded-[18px] border border-brand-charcoal/10 bg-white object-cover shadow-[0_30px_60px_-36px_rgba(90,70,40,0.5)]" data-id="element-20" data-mp="39zx0ko4ligpc" />
          </figure>)}
      </div>

      <ArrowButton side="left" label="Previous page" onClick={() => goTo(active - 1)} data-id="element-21" data-mp="2ma2t6m31pkch">
        <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-22" data-mp="305diljyitd9g" />
      </ArrowButton>
      <ArrowButton side="right" label="Next page" onClick={() => goTo(active + 1)} data-id="element-23" data-mp="2var5nccsjcn9">
        <ChevronRightIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-24" data-mp="27l6hmf1s5p74" />
      </ArrowButton>

      <div className="mt-6 flex flex-col items-center gap-3" data-id="element-25" data-mp="22ltxw367gy8r">
        <p className="text-center font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-charcoal/55" aria-live="polite" data-id="element-26" data-mp="i4t3g95ob411">
          {insidePages[active].caption}
        </p>
        <div className="flex items-center gap-2.5" data-id="element-27" data-mp="968rdqumbsls">
          {insidePages.map((page, index) => <button key={page.src} type="button" aria-label={`Show ${page.caption}`} aria-current={index === active} onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta focus-visible:ring-offset-2 ${index === active ? 'w-8 bg-brand-cta' : 'w-2 bg-brand-charcoal/20 hover:bg-brand-charcoal/35'}`} data-id="element-28" data-mp="e5byrhy2mawx" data-mpi={index} />)}
        </div>
      </div>
    </div>;
}
function ArrowButton({
  side,
  label,
  onClick,
  children
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className={`absolute top-[calc(50%-2.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-brand-charcoal/10 bg-white text-brand-charcoal shadow-[0_10px_24px_-12px_rgba(42,37,32,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-brand-ivory active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cta md:flex ${side === 'left' ? '-left-6' : '-right-6'}`} data-id="element-29" data-mp="3s3nxcm0kwo67">
      {children}
    </button>;
}